
import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus } from "lucide-react";
import { useCreateCompany, useUpdateCompany } from "@/hooks/useCompanies";
import { toast } from "sonner";
import { CompanyLogoUpload } from "@/components/companies/CompanyDialog/CompanyLogoUpload";
import { supabase } from "@/integrations/supabase/client";

interface QuickCompanyAddProps {
  onCompanyAdded: (companyId: string, companyName: string) => void;
}

export const QuickCompanyAdd = ({ onCompanyAdded }: QuickCompanyAddProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState({
    name_ar: "",
    mobile: "",
    address_ar: ""
  });
  const [isUploading, setIsUploading] = useState(false);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);

  const createCompanyMutation = useCreateCompany();
  const updateCompanyMutation = useUpdateCompany();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    } else {
      setLogoFile(null);
      setLogoPreview(null);
    }
  };

  const uploadLogo = async (companyId: string, file: File) => {
    setIsUploading(true);
    try {
        const filePath = `public/${companyId}/${Date.now()}-${file.name}`;
        const { error: uploadError } = await supabase.storage
            .from('company-logos')
            .upload(filePath, file);

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
            .from('company-logos')
            .getPublicUrl(filePath);

        await updateCompanyMutation.mutateAsync({
            id: companyId,
            logo_url: publicUrl,
        });

    } catch (error) {
        console.error("Error uploading logo:", error);
        toast.error("فشل في تحميل الشعار.");
        throw error;
    } finally {
        setIsUploading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name_ar.trim()) {
      toast.error("اسم الشركة مطلوب");
      return;
    }

    try {
      const newCompany = await createCompanyMutation.mutateAsync(formData);

      if (logoFile) {
        await uploadLogo(newCompany.id, logoFile);
      }

      toast.success("تم إضافة الشركة بنجاح");
      onCompanyAdded(newCompany.id, newCompany.name_ar);
      setFormData({ name_ar: "", mobile: "", address_ar: "" });
      setLogoFile(null);
      setLogoPreview(null);
      setIsOpen(false);
    } catch (error) {
      console.error("Error creating company:", error);
      toast.error("حدث خطأ أثناء إضافة الشركة");
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setFormData({ name_ar: "", mobile: "", address_ar: "" });
      setLogoFile(null);
      setLogoPreview(null);
    }
    setIsOpen(open);
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button type="button" variant="outline" size="sm" dir="rtl">
          <Plus className="w-4 h-4 mr-2" />
          إضافة شركة جديدة
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md" dir="rtl">
        <DialogHeader>
          <DialogTitle>إضافة شركة جديدة</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="company_name">اسم الشركة *</Label>
            <Input
              id="company_name"
              value={formData.name_ar}
              onChange={(e) => setFormData(prev => ({ ...prev, name_ar: e.target.value }))}
              placeholder="اسم الشركة"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="company_mobile">رقم الجوال</Label>
            <Input
              id="company_mobile"
              value={formData.mobile}
              onChange={(e) => setFormData(prev => ({ ...prev, mobile: e.target.value }))}
              placeholder="+966xxxxxxxxx"
              type="tel"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="company_address">العنوان</Label>
            <Input
              id="company_address"
              value={formData.address_ar}
              onChange={(e) => setFormData(prev => ({ ...prev, address_ar: e.target.value }))}
              placeholder="عنوان الشركة"
            />
          </div>

          <div className="space-y-2">
            <Label>شعار الشركة</Label>
            <div className="flex items-center gap-4">
              {logoPreview ? (
                <img src={logoPreview} alt="معاينة الشعار" className="w-16 h-16 rounded-md object-contain border bg-white" />
              ) : (
                <div className="w-16 h-16 rounded-md bg-gray-100 flex items-center justify-center text-xs text-gray-500 text-center p-1">لا يوجد شعار</div>
              )}
              <div className="flex-1">
                <Input
                  id="logo-upload"
                  type="file"
                  onChange={handleFileChange}
                  disabled={isUploading}
                  className="text-xs h-9"
                  accept="image/*"
                />
                {isUploading && (
                  <div className="flex items-center gap-2 text-xs text-blue-600 mt-1">
                    <div className="w-3 h-3 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                    <span>جاري الرفع...</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={() => setIsOpen(false)}>
              إلغاء
            </Button>
            <Button type="submit" disabled={createCompanyMutation.isPending || isUploading}>
              {createCompanyMutation.isPending || isUploading ? "جاري الحفظ..." : "حفظ"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
